import { useRazorpay } from 'react-razorpay';
import { useNavigate } from 'react-router-dom';
import { useCallback, useRef, useState } from 'react';
import { EVENTS, PROPERTIES, trackEvent } from '@/services/posthog';
import { confirmGuestPayment, createGuestOrder } from '@/services/slaydAPI';
import useToastStore from '@/stores/ToastStore';
import { ConfirmPaymentStatus, type GuestOrder } from '@/types/Order.types';
import type { Product } from '@/types/Product.types';

declare const process: {
  env: {
    RAZORPAY_KEY_ID?: string;
  };
};

type PaymentStatus =
  | 'idle'
  | 'creating_order'
  | 'awaiting_payment'
  | 'confirming_payment'
  | 'success'
  | 'error';

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_signature: string;
  razorpay_order_id: string;
}

export interface UseRazorpayWebResult {
  initiatePayment: (
    product: Product,
    size: string | undefined
  ) => Promise<GuestOrder | null>;
  status: PaymentStatus;
  error: string | null;
  isSDKLoading: boolean;
}

export const useRazorpayWeb = (): UseRazorpayWebResult => {
  const { Razorpay, isLoading: isSDKLoading } = useRazorpay();
  const [status, setStatus] = useState<PaymentStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const isInProgressRef = useRef(false);
  const navigate = useNavigate();
  const showToast = useToastStore((state) => state.showToast);

  const initiatePayment = useCallback(
    async (
      product: Product,
      size: string | undefined
    ): Promise<GuestOrder | null> => {
      if (isInProgressRef.current || isSDKLoading || !Razorpay) return null;

      isInProgressRef.current = true;
      setStatus('creating_order');
      setError(null);

      let guestOrder: GuestOrder | undefined;
      let paymentStep: 'order_creation' | 'razorpay' | 'confirmation' =
        'order_creation';

      try {
        // Step 1: Create Guest Order on backend
        guestOrder = await createGuestOrder({
          productId: product.id,
          quantity: 1,
          ...(size ? { size } : {}),
        });

        paymentStep = 'razorpay';
        setStatus('awaiting_payment');

        trackEvent(EVENTS.ORDER_CREATED, {
          [PROPERTIES.GUEST_ORDER_ID]: guestOrder.guestOrderId,
          [PROPERTIES.PRODUCT_ID]: product.id,
          [PROPERTIES.SELECTED_SIZE]: size || '',
        });

        const currentGuestOrderId = guestOrder.guestOrderId;

        // Step 2: Configure Razorpay Web options
        // razorpayAmount is returned in Rupees, convert to paise
        const amountInPaise = Math.round(
          parseFloat(guestOrder.razorpayAmount || '0') * 100
        );

        const options = {
          key: (process.env.RAZORPAY_KEY_ID as string) || '',
          amount: amountInPaise,
          currency: 'INR' as const,
          name: 'Slayd Originals',
          description: product.name,
          order_id: guestOrder.razorpayOrderId || '',
          theme: { color: '#ffa8cd' }, // Slayd primary pink
          prefill: {
            name: '',
            email: '',
            contact: '',
          },
          handler: async (response: RazorpayResponse) => {
            paymentStep = 'confirmation';
            setStatus('confirming_payment');

            trackEvent(EVENTS.RAZORPAY_SUCCESS, {
              [PROPERTIES.GUEST_ORDER_ID]: currentGuestOrderId,
              [PROPERTIES.PAYMENT_ID]: response.razorpay_payment_id,
            });

            try {
              // Step 3: Verify payment with backend
              await confirmGuestPayment(currentGuestOrderId, {
                paymentStatus: ConfirmPaymentStatus.SUCCESS,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              });
              setStatus('success');

              trackEvent(EVENTS.PAYMENT_CONFIRMED, {
                [PROPERTIES.GUEST_ORDER_ID]: currentGuestOrderId,
                [PROPERTIES.PRODUCT_ID]: product.id,
              });

              showToast('Payment successful!');
              navigate(`/order-placed?guestOrderId=${currentGuestOrderId}`);
            } catch (err) {
              console.error('Payment confirmation failed:', err);
              setStatus('error');
              setError(
                'Failed to confirm payment status. Please contact support.'
              );
              showToast('Verification failed. Staging order status.');

              const confirmFailureReason =
                err instanceof Error ? err.message : 'unknown';
              trackEvent(EVENTS.PAYMENT_CONFIRM_FAILED, {
                [PROPERTIES.GUEST_ORDER_ID]: currentGuestOrderId,
                [PROPERTIES.PRODUCT_ID]: product.id,
                [PROPERTIES.FAILURE_REASON]: confirmFailureReason,
              });
            }
          },
          modal: {
            ondismiss: () => {
              isInProgressRef.current = false;
              setStatus('idle');
              showToast('Payment was cancelled');

              trackEvent(EVENTS.RAZORPAY_CANCELLED, {
                [PROPERTIES.GUEST_ORDER_ID]: currentGuestOrderId,
                [PROPERTIES.PRODUCT_ID]: product.id,
              });

              confirmGuestPayment(currentGuestOrderId, {
                paymentStatus: ConfirmPaymentStatus.FAILED,
              }).catch((e) =>
                console.error('Error reporting cancel status', e)
              );
            },
          },
        };

        // Step 4: Open Razorpay Dialog
        trackEvent(EVENTS.RAZORPAY_OPENED, {
          [PROPERTIES.GUEST_ORDER_ID]: guestOrder.guestOrderId,
          [PROPERTIES.PRODUCT_ID]: product.id,
        });

        const rzp = new Razorpay(options);
        rzp.open();
        return guestOrder;
      } catch (err: unknown) {
        console.error('Checkout error:', err);

        let errMsg = 'Payment failed';
        if (err instanceof Error) {
          errMsg = err.message;
        } else if (err && typeof err === 'object' && 'response' in err) {
          const response = (
            err as { response?: { data?: { message?: string } } }
          ).response;
          if (response?.data?.message) {
            errMsg = response.data.message;
          }
        }

        setError(errMsg);
        setStatus('error');
        showToast(errMsg);

        const failureReason = err instanceof Error ? err.message : 'unknown';
        if (paymentStep === 'order_creation') {
          trackEvent(EVENTS.ORDER_CREATE_FAILED, {
            [PROPERTIES.PRODUCT_ID]: product.id,
            [PROPERTIES.FAILURE_REASON]: failureReason,
          });
        } else if (paymentStep === 'razorpay') {
          trackEvent(EVENTS.RAZORPAY_FAILURE, {
            [PROPERTIES.GUEST_ORDER_ID]: guestOrder?.guestOrderId || 0,
            [PROPERTIES.PRODUCT_ID]: product.id,
            [PROPERTIES.FAILURE_REASON]: failureReason,
          });
        }

        // Cancel guest order if created but failed on checkout initialization
        if (guestOrder) {
          confirmGuestPayment(guestOrder.guestOrderId, {
            paymentStatus: ConfirmPaymentStatus.FAILED,
          }).catch((e) => console.error('Error reporting failed status', e));
        }

        throw err;
      } finally {
        if (status !== 'awaiting_payment') {
          isInProgressRef.current = false;
        }
      }
    },
    [Razorpay, isSDKLoading, navigate, showToast, status]
  );

  return { initiatePayment, status, error, isSDKLoading };
};
export default useRazorpayWeb;
